/**
 * Selected keyframes with their context (W2-06): track, neighbours, owning line, line kind and the friendly unit of
 * the property, as one memoised selector result for the key, segment and text segment sections.
 */

import { useMemo } from 'react';
import type { AppServices } from '../../../app/registry/types';
import { kindOfLine } from '../../../core/model/lineKind';
import { fromFriendly, toFriendly, Units } from '../../../core/model/units';
import type { MessageKey } from '../../../i18n/types';
import type { ChartDoc, DocIndexes, FriendlyUnit, KeyId, LineKind } from '../../../core/model/types';
import type { DocStore } from '../../../state/types';
import { useDocValue } from '../hooks';
import { unitLabels, unitStep } from '../labels';
import { keyContext, tidy, type KeyContext } from '../model';
import { unitToggleFor, type SectionBase } from '../parts';

type Translate = (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string;

export interface KeyInfo {
  readonly ctx: KeyContext;
  readonly kind: LineKind;
  readonly uiMover: boolean;
  /** Friendly unit of a number property ('raw' for colours, texts and effect variables). */
  readonly unit: FriendlyUnit;
  readonly valueType: 'number' | 'rgb' | 'string' | 'other';
  /** The layer's editor name, if any. */
  readonly layerName: string | null;
}

function infoOf(doc: ChartDoc, idx: DocIndexes, id: KeyId): KeyInfo | null {
  const ctx = keyContext(doc, idx, id);
  if (ctx === null) return null;
  const line = ctx.line === null ? undefined : doc.lines.get(ctx.line);
  const kind = line === undefined ? 'normal' : kindOfLine(doc, idx, line);
  const uiMover = line?.attachUI != null;
  const vt = ctx.track.valueType;
  const valueType = vt === 'number' || vt === 'rgb' || vt === 'string' ? vt : 'other';
  const unit = ctx.prop === null || valueType !== 'number' ? 'raw' : Units.unitFor(ctx.prop, { kind, uiMover });
  const layerName = line?.editor.layerNames[ctx.layer] ?? null;
  return { ctx, kind, uiMover, unit, valueType, layerName };
}

function sameInfo(a: KeyInfo, b: KeyInfo): boolean {
  return (
    a.ctx.key === b.ctx.key &&
    a.ctx.prev === b.ctx.prev &&
    a.ctx.next === b.ctx.next &&
    a.ctx.track === b.ctx.track &&
    a.kind === b.kind &&
    a.uiMover === b.uiMover &&
    a.layerName === b.layerName &&
    a.ctx.index === b.ctx.index
  );
}

function sameInfos(a: readonly KeyInfo[], b: readonly KeyInfo[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!sameInfo(a[i] as KeyInfo, b[i] as KeyInfo)) return false;
  return true;
}

/** Contexts of the keys `ids` (missing ones dropped), re-selected when keys, tracks, lines or assets change. */
export function useKeyInfos(store: DocStore, ids: readonly KeyId[]): readonly KeyInfo[] {
  const key = ids.join(',');
  const selector = useMemo(() => {
    const list = key === '' ? [] : (key.split(',').map(Number) as KeyId[]);
    return (doc: ChartDoc, idx: DocIndexes): KeyInfo[] =>
      list.map((id) => infoOf(doc, idx, id)).filter((x): x is KeyInfo => x !== null);
  }, [key]);
  return useDocValue(store, selector, ['keys', 'tracks', 'lines', 'assets'], sameInfos);
}

/** The common unit of the keys, or 'raw' when they differ (values are then shown in RPE raw units). */
export function commonUnit(infos: readonly KeyInfo[]): { readonly unit: FriendlyUnit; readonly mixed: boolean } {
  const first = infos[0];
  if (first === undefined) return { unit: 'raw', mixed: false };
  const mixed = infos.some((i) => i.unit !== first.unit);
  return { unit: mixed ? 'raw' : first.unit, mixed };
}

/** How a number field shows values of a friendly unit (raw when the user prefers RPE units or the unit is raw). */
export interface UnitSetup {
  /** Unit the field shows (and expressions evaluate in). */
  readonly shown: FriendlyUnit;
  readonly toShown: (raw: number) => number;
  readonly toRaw: (shown: number) => number;
  readonly unitProps:
    | { readonly unit: string }
    | {
        readonly unitToggle: {
          readonly raw: string;
          readonly friendly: string;
          readonly mode: 'raw' | 'friendly';
          onToggle(): void;
        };
      };
  readonly step: number;
}

export function unitSetup(t: Translate, services: AppServices, base: SectionBase, unit: FriendlyUnit): UnitSetup {
  const raw = base.rawUnits || unit === 'raw';
  const shown: FriendlyUnit = raw ? 'raw' : unit;
  const labels = unitLabels(t, unit);
  const toggle = unitToggleFor(services, base, labels.friendly, labels.raw);
  return {
    shown,
    toShown: (v) => tidy(toFriendly(shown, v)),
    toRaw: (v) => tidy(fromFriendly(shown, v)),
    unitProps: toggle === undefined ? { unit: raw ? labels.raw : labels.friendly } : { unitToggle: toggle },
    step: unitStep(unit, raw),
  };
}
